// Power-up pickups: spinning glowing octahedra on a pavement lane, one type
// spawned every ~20s (timer lives in the Spawner). Collect radius 1.0
// (v1 game3d.js:910).

import * as THREE from 'three';
import { LANES, SPAWN_DISTANCE } from '@/config/tuning';
import { POWER_UP_TYPES, type PowerUpId } from '@/content/powerups';
import type { Rng } from '@/core/rng';

export interface PowerUpPickup {
  mesh: THREE.Mesh;
  typeId: PowerUpId;
}

export class PowerUpSystem {
  readonly pickups: PowerUpPickup[] = [];
  private readonly geo = new THREE.OctahedronGeometry(0.32);

  constructor(
    private readonly scene: THREE.Scene,
    private readonly rng: Rng,
  ) {}

  spawn(typeId: PowerUpId): void {
    const def = POWER_UP_TYPES[typeId];
    const material = new THREE.MeshLambertMaterial({
      color: def.color,
      emissive: def.emissiveColor,
      emissiveIntensity: 0.6,
    });
    material.userData.noToon = true; // Stays shiny (v1 toon opt-out)
    const mesh = new THREE.Mesh(this.geo, material);
    const lane = this.rng.next() < 0.5 ? 0 : 3;
    mesh.position.set(LANES[lane], 0.8, SPAWN_DISTANCE);
    mesh.userData.noToon = true;
    this.scene.add(mesh);
    this.pickups.push({ mesh, typeId });
  }

  update(dt: number, gameSpeed: number): void {
    const dz = gameSpeed * dt * 60;
    for (let i = this.pickups.length - 1; i >= 0; i--) {
      const pickup = this.pickups[i];
      if (!pickup) continue;
      pickup.mesh.position.z += dz;
      pickup.mesh.rotation.y += 2.5 * dt;
      pickup.mesh.position.y = 0.8 + Math.sin(pickup.mesh.rotation.y) * 0.1; // Gentle bob
      if (pickup.mesh.position.z > 15) this.despawn(i);
    }
  }

  despawn(index: number): void {
    const pickup = this.pickups[index];
    if (!pickup) return;
    this.scene.remove(pickup.mesh);
    (pickup.mesh.material as THREE.Material).dispose();
    this.pickups.splice(index, 1);
  }

  clear(): void {
    for (let i = this.pickups.length - 1; i >= 0; i--) this.despawn(i);
  }
}
